<template>
  <v-card flat>
    <v-dialog
      v-model="showDeleteInput"
      max-width="480px"
    >
      <videon-card heading="Delete Input" showClose="true" @close="showDeleteInput = false">
        <v-card-text>
          <v-alert
            type="info"
            text
            icon="mdi-lock-outline"
            class="mt-3"
            v-if="!isDynamicSupported"
          >
            Adding and removing inputs dynamically requires a minimum of daemon version {{ $versioning.dynamicSupportRequirements.minimumDaemonVersion.original }} 
            and cloud agent version {{ $versioning.dynamicSupportRequirements.minimumAgentVersion.original }}.
          </v-alert>
          <v-form v-else @submit.prevent="confirmDeleteInput">
            <p class="subtitle-1 mt-3">
              Are you sure you want to delete this input?
            </p>
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showDeleteInput = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="confirmDeleteInput"
              >
                Delete
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    <v-dialog
      v-model="showNewInput"
      max-width="700px"
    >
      <videon-card heading="Create Input" showClose="true" @close="showNewInput = false">
        <v-card-text>
          <v-alert
            type="info"
            text
            icon="mdi-lock-outline"
            class="mt-3"
            v-if="!isDynamicSupported"
          >
            Adding and removing inputs dynamically requires newer software. Please update to at least daemon version <strong>{{ $versioning.dynamicSupportRequirements.minimumDaemonVersion.original }}</strong>
            and cloud agent version <strong>{{ $versioning.dynamicSupportRequirements.minimumAgentVersion.original }}</strong>.
          </v-alert>
          
          <v-form v-else @submit.prevent="createInput">
            <v-row class="mb-3">
              <v-col cols="8">
                <v-select
                  v-model="newInput.type"
                  :items="newInputTypes"
                  @change="newInputTypeChange"
                  label="Input Type"
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="4">
                <v-select
                  v-if="newInput.type == 'hardware'"
                  v-model="newInput.hw_channel"
                  :items="hw_channelValues"
                  label="Hardware Channel"
                  @change="newInputTypeChange"
                  no-data-text="No unused hardware channels"
                  outlined
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            
            <div class="mt-5" v-if="newInput">
              <hardware-input-document v-if="newInput.type == 'hardware'" :input="newInput" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" :isNew="true" @configUpdate="newInputUpdate" :key="newInput.id"/>
              <gstreamer-input-document v-else-if="newInput.type == 'gstreamer'" :input="newInput" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" :isNew="true" @configUpdate="newInputUpdate" :key="newInput.id"/>
            </div>
            
            <div class="text-right">
              <v-btn
                class="ma-1"
                color="secondary"
                @click="showNewInput = false"
                text
              >
                Cancel
              </v-btn>
              <v-btn
                class="ma-1"
                color="primary"
                @click="createInput"
                :disabled="newInputHasValidationErrors"
              >
                Create Input
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    
    <v-card-title>
      Input Configuration
      <v-spacer/>
      <v-btn
        small
        color="primary"
        outlined
        tooltip="New Input"
        @click="newInputShow()"
        :disabled="!canEdit || !hasFeature('daemon.input.video.limit', instanceCount)"
      >
        <v-icon>mdi-plus</v-icon> Create Input
      </v-btn>
    </v-card-title>
    <v-card-text>
      <v-alert type="info" v-if="xmlConfig">These settings are currently managed by the active XML configuration.</v-alert>
      
      <v-data-table
        :headers="headers"
        :items="documents"
        :single-expand="true"
        :expanded.sync="expanded"
        item-key="id"
        :items-per-page="100"
        show-expand
        hide-default-footer
        @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
      >
        <template
          v-slot:item.id="{ item }"
        >
          <span :class="(item.new) ? 'text--disabled' : ''">{{ inputName(item) }}</span>
        </template>
        <template
          v-slot:item.type="{ item }"
        >
          <span :class="(item.new) ? 'text--disabled' : ''">{{ inputType(item) }}</span>
        </template>
        <template
          v-slot:item.status="{ item }"
        >
          <span v-if="item.new" class="text--disabled">Not Created</span>
          <input-status-label v-else :input="item" />
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length" class="px-8 pt-8 pb-5">
            <hardware-input-document v-if="item.type == 'hardware'" :input="item" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteInput="deleteInput(item)" :key="item.id"/>
            <gstreamer-input-document v-else-if="item.type == 'gstreamer'" :input="item" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteInput="deleteInput(item)" :key="item.id"/>
            <generic-input-document v-else :input="item" :documents="documents" :device_guid="device_guid" :canEdit="canEdit" @configUpdate="configUpdate" @deleteInput="deleteInput(item)" :key="item.id"/>
          </td>
        </template>
      </v-data-table>
      
      <div class="mt-5 text-right" v-if="canEdit">
        <v-btn
          color="secondary"
          text
          class="ma-1"
          @click="revertShadow()"
          :disabled="!hasChanges"
        >
          Revert
        </v-btn>
        <v-btn
          color="primary"
          class="ma-1"
          @click="saveShadow()"
          :disabled="hasChanges == false || hasValidationError == true"
        >
          Save
        </v-btn>
      </div>
    </v-card-text>
  </v-card>
</template>
<script>
  import { mapGetters } from 'vuex'
  import ShadowMixins from './mixins/mixins.js'
  
  import HardwareInputDocument from './documents/HardwareInputDocument.vue'
  import GstreamerInputDocument from './documents/GStreamerInputDocument.vue'
  import GenericInputDocument from './documents/GenericInputDocument.vue'
  
  export default {
    name: 'EdgecasterInputs',
    
    components: {
      HardwareInputDocument,
      GstreamerInputDocument,
      GenericInputDocument
    },
    
    mixins:[ShadowMixins],
    
    props: ['deviceShadow', 'device_guid'],
    
    data() {
      return {
        gstreamerDaemonVersion: this.$helpers.parseVersion('device', 'v11.3.0'),
        
        expanded: [],
        
        documents: [],
        hasChanges: false,
        expectingChanges: false,
        
        hasValidationError: false,
        
        showDeleteInput: false,
        selectedInput: false,
        
        showNewInput: false,
        
        newInput:false,
        newInputHasValidationErrors: false,
        
        headers: [
          { text: 'Name', align: 'start', value: 'id', sortable: true },
          { text: 'Input Type', align: 'start text-capitalize', value: 'type', sortable: true },
          { text: 'Input Status', align: 'start', value: 'status', sortable: true },
        ],
      }
    },
    watch: {
      deviceShadow: {
        handler() {
          // only overwrite if we dont have any documents to start with
          if (this.expectingChanges || this.expanded.length == 0 || this.documents.length == 0) {
            var documents = this.deviceShadow.editableStateCopy()
            if (documents) {
              this.documents = documents
              
              // reset expectingChanges
              setTimeout(() => {
                this.expectingChanges = false
              }, 5000)
            }
          }
        }, 
        deep: true
      }
    },
    computed: {
      ...mapGetters('user', ['isDeviceAdmin']),
      
      device() {
        return this.$devices.getDevice(this.device_guid)
      },
      
      canEdit() {
        if (this.xmlConfig || this.deviceShadow.updatePending) {
          return false
        }
        
        if (!this.device.hasFeature('services.cloud.device_config_tab.enabled') && !this.isDeviceAdmin) {
          return false
        }
        
        return this.$devices.getDevice(this.device_guid).canEdit()
      },
      
      xmlConfig() {
        return this.$devices.getDevice(this.device_guid).hasXMLConfig()
      },
      
      isDynamicSupported() {
        return this.$versioning.isDynamicSupported(this.device)
      },
      
      newInputTypes() {
        var newInputTypes = [
          { 'id': false, 'value': 'hardware', 'text': 'Hardware' }
        ]
        
        if (
          this.device.product_name == 'edgecaster_max' 
          && this.$helpers.versionCheck(this.gstreamerDaemonVersion, this.device.daemon_version)
          && this.device.hasFeature('daemon.input.video.gstreamer.limit')
        ) {
          newInputTypes.push({ 'value': 'gstreamer', 'text': 'GStreamer' })
        }
        
        return newInputTypes
      },
      
      hw_channelValues() {
        var channelValues = []
        
        if (!this.isHWChannelUsed(0)) {
          channelValues.push({ 'value': 0, 'text': 'Channel 0' })
        }
        
        if (this.device.product_name == 'edgecaster_max' && !this.isHWChannelUsed(1)) {
          channelValues.push({ 'value': 1, 'text': 'Channel 1' })
        }
        
        return channelValues
      }
    },
    
    mounted() {
      var documents = this.deviceShadow.editableStateCopy()
      if (documents) {
        this.documents = documents
        this.hasChanges = false
      }
    },
    
    methods: {
      inputName(item) {
        if (item.config && item.config.name) {
          return this.$helpers.htmlDecode(item.config.name)
        } else if (item.new) {
          return 'New Input'
        }
        return 'Input #' + item.id
      },
      
      inputType(item) {
        if (item.type) {
          return this.$helpers.getLabel(item.type)
        }
        return 'Hardware'
      },
      
      isHWChannelUsed(hw_channel) {
        return (this.documents.find(input => input.hw_channel === hw_channel) ? true : false)
      },
      
      nextHWChannel() {
        var channels = this.hw_channelValues
        
        if (channels.length > 0) {
          return channels[0].value
        }
        
        return false
      },
      
      newInputShow() {
        this.newInput =  {  'id': 'new-' +  new Date().getTime(), 'type': 'hardware', 'hw_channel': this.nextHWChannel(), 'new': true }
        this.showNewInput = true
      },
      
      newInputTypeChange() {
        console.log('EdgecasterInputs newInputTypeChange', this.newInput)
        this.$delete(this.newInput, 'config')
      },
      
      newInputUpdate(config, hasValidationError = false) {
        console.log('EdgecasterInputs newInputUpdate', config, hasValidationError)
        this.newInput.config = config
        this.newInputHasValidationErrors = hasValidationError
      },
      
      createInput() {
        if (!this.newInputHasValidationErrors) {
          // fix for transient vars
          if (!this.newInput.config.name) {
            this.newInput.config.name = ''
          }
          
          console.log('EdgecasterInputs createInput', this.newInput)
          
          this.documents.push(Object.assign({}, this.newInput))
          this.hasChanges = true
          this.expanded = []
          
          this.showNewInput = false
        }
      },
      
      deleteInput(item) {
        this.selectedInput = item
        this.showDeleteInput = true
      },
      
      confirmDeleteInput() {
        var inputIndex = this.documents.indexOf(this.selectedInput)
        if (inputIndex !== -1) {
          console.log('EdgecasterInputs confirmDeleteInput deleting input', this.selectedInput)
          
          this.documents.splice(inputIndex, 1)
          this.hasChanges = true
          this.expanded = []
        }
        this.showDeleteInput = false
      },
      
      
      
      configUpdate(config, hasValidationError = false) {
        this.hasChanges = true
        this.hasValidationError = hasValidationError
      },
      
      saveShadow() {
        var state = this.documents.map((input) => {
          var newInput = {'id': (input.new) ? false : input.id, 'type': input.type, 'config': input.config}
          
          if (input.type == 'hardware' && input.hw_channel !== undefined) {
            newInput.hw_channel = input.hw_channel
            newInput.config.sdi_audio_ignore_phase = false
          }
          
          return newInput
        })
        
        this.deviceShadow.saveShadow(state)
        this.expanded = []
        this.hasChanges = false
        this.expectingChanges = true
      },
      
      revertShadow() {
        this.documents = this.deviceShadow.editableStateCopy()
        this.hasChanges = false
      }
    }
  }
</script>